/**
 * ComplianceTrophy — compliance status indicator + click-through board.
 *
 * Iter-134 (Sections 2 + 3):
 *   • Regional Manager (role='manager') is EXCLUDED — the surrounding
 *     dashboards also guard with `user.role !== 'manager'`.
 *   • Trophy is a button — clicking it (a) refetches
 *     /api/staff/compliance-status/me immediately (no 60s wait) and
 *     (b) opens a read-only board with the 4 admin-managed factors:
 *     Time Performance · Job Completion · Safety Record · Attendance.
 *   • Field staff only (supervisor / crew_leader / agent). Residents
 *     get the trophy but not the board (their surface is elsewhere).
 *   • 60-second poll still runs alongside.
 *
 * Iter-135 Section 11 · Residents — tapping the trophy opens the same
 * operations record board (streaks, totals, milestone badges) with a
 * fresh load. Read-only.
 */
import { useEffect, useState, useCallback } from 'react';
import { Trophy, X } from 'lucide-react';
import { apiSafeFetch } from '../lib/apiFetch';
import ResidentOperationRecord from '../pages/resident/ResidentOperationRecord';
import { StaffRewardProgress } from './rewards/StaffRewardProgress';
import { ResidentRewards } from './rewards/ResidentRewards';

const STATUS_COLORS = { good: '#22c55e', at_risk: '#f59e0b', violation: '#ef4444', flagged: '#ef4444' };
const STATUS_TITLES = { good: 'Good standing', at_risk: 'At risk', violation: 'Active violation' };
const FACTOR_LABELS = {
  time_performance: 'Time Performance',
  job_completion: 'Job Completion',
  safety_record: 'Safety Record',
  attendance: 'Attendance',
};
const FACTOR_STATE_LABEL = { good: 'Good', at_risk: 'At Risk', flagged: 'Flagged' };

const RESIDENT_ROLES = ['tenant', 'tenant_resident', 'resident'];
// Section 3 (iter-136) — trophy renders for these role combinations.
// Iter-175 · Phase 2 — 'manager' (Regional Manager) + 'property_manager'
// added so their header cluster gets the trophy too (previously hidden).
// Site Agents must additionally have role_type in { Valet, Cleaner } —
// enforced against the role_type prop.
const STAFF_ROLES = ['crew_leader', 'supervisor', 'manager', 'regional_manager', 'property_manager'];
const AGENT_ROLE_TYPES = new Set(['valet', 'cleaner', 'cleaners']);

export default function ComplianceTrophy({ role, roleType, primaryService }) {
  const [status, setStatus] = useState(null);
  const [data, setData] = useState(null);
  const [factors, setFactors] = useState(null);
  const [open, setOpen] = useState(false);

  const roleLc = (role || '').toLowerCase();
  const roleTypeLc = (roleType || '').toLowerCase();
  // Section 6 (iter-137) — Site Agents whose role_type was never set on
  // creation still see the trophy when their primary_service is valet
  // or cleaning. role_type is the source of truth when populated; the
  // service line acts as a fallback for legacy accounts.
  const primaryLc = (primaryService || '').toLowerCase();
  const serviceLineMatch = AGENT_ROLE_TYPES.has(roleTypeLc) ||
    (['valet', 'cleaning', 'cleaners'].includes(primaryLc));
  const isAgent = roleLc === 'agent' && serviceLineMatch;
  const isStaff = STAFF_ROLES.includes(roleLc) || isAgent;
  const isResident = RESIDENT_ROLES.includes(roleLc);

  const fetchStatus = useCallback(async () => {
    let url = null;
    if (isResident) url = '/api/resident-rewards/me';
    else if (isStaff && roleLc !== 'property_manager') url = '/api/staff-rewards/me';
    else if (isStaff) url = '/api/staff/compliance-status/me';
    if (!url) return;
    const { data } = await apiSafeFetch(url);
    setData(data || null);
    if (!data) {
      // Iter-175 · Phase 2 — if the compliance endpoint returns no data
      // (e.g. regional manager without a compliance profile), still
      // render the trophy in default 'good' state so the cluster stays
      // consistent across roles.
      if (isStaff) setStatus('pending');
      return;
    }
    if (isStaff && roleLc !== 'property_manager') { setStatus(data.standard_percent == null ? 'pending' : data.standard_percent >= 75 ? 'good' : 'at_risk'); return; }
    if (isResident) { setStatus(data.compliance_percent == null ? 'pending' : data.compliance_percent >= 85 ? 'good' : 'at_risk'); return; }
    if (data.status) setStatus(data.status);
    else if (data.open_violations > 0) setStatus('violation');
    else if ((data.totals?.violations_on_account || 0) > 0) setStatus('at_risk');
    else setStatus('good');
    if (data.factors) setFactors(data.factors);
  }, [isResident, isStaff, roleLc]);

  useEffect(() => {
    let alive = true;
    fetchStatus();
    const onChanged = () => { if (alive) fetchStatus(); };
    window.addEventListener('necs-compliance-changed', onChanged);
    const timer = setInterval(() => { if (alive) fetchStatus(); }, 60000);
    return () => {
      alive = false;
      window.removeEventListener('necs-compliance-changed', onChanged);
      clearInterval(timer);
    };
  }, [fetchStatus]);

  if (!status) return null;

  const handleClick = () => {
    fetchStatus();
    if (isStaff || isResident) setOpen(true);
  };

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        className="p-2 inline-flex bg-transparent border-0 cursor-pointer"
        data-testid="compliance-trophy"
        data-status={status}
        data-tier={data?.trophy?.key || 'legacy'}
        title={`${STATUS_TITLES[status] || status} · Click to refresh${isStaff || isResident ? ' & view board' : ''}`}
        aria-label={`Compliance: ${STATUS_TITLES[status] || status}`}
      >
        <Trophy className="h-5 w-5" style={{ color: data?.trophy?.color || STATUS_COLORS[status] }} />
      </button>

      {open && isResident && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={() => setOpen(false)}
          data-testid="resident-record-overlay"
        >
          <div
            className="w-full max-w-md rounded-2xl border border-zinc-700 bg-zinc-900 p-5 shadow-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="resident-record-board"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <Trophy className="h-6 w-6" style={{ color: data?.trophy?.color || STATUS_COLORS[status] }} />
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-zinc-500">Operations Record</p>
                  <p className="text-lg font-bold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                    {(data?.trophy?.label || STATUS_TITLES[status] || status).toUpperCase()}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="p-1 rounded hover:bg-zinc-800 text-zinc-400 hover:text-white"
                aria-label="Close operations record"
                data-testid="resident-record-close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <ResidentRewards prefix="trophy-resident-rewards" />
          </div>
        </div>
      )}

      {open && isStaff && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={() => setOpen(false)}
          data-testid="compliance-board-overlay"
        >
          <div
            className="w-full max-w-xl max-h-[85dvh] overflow-y-auto rounded-2xl border border-zinc-700 bg-zinc-900 p-5 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="compliance-board"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <Trophy className="h-6 w-6" style={{ color: STATUS_COLORS[status] }} />
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-zinc-500">Compliance Board</p>
                  <p className="text-lg font-bold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                    {(STATUS_TITLES[status] || status).toUpperCase()}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="p-1 rounded hover:bg-zinc-800 text-zinc-400 hover:text-white"
                aria-label="Close compliance board"
                data-testid="compliance-board-close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            {roleLc !== 'property_manager' ? <StaffRewardProgress prefix="trophy-staff-progress" /> : <><ul className="space-y-2">
              {Object.entries(FACTOR_LABELS).map(([key, label]) => {
                const state = (factors && factors[key]?.state) || 'good';
                const color = STATUS_COLORS[state] || STATUS_COLORS.good;
                return (
                  <li
                    key={key}
                    data-testid={`compliance-factor-${key}`}
                    data-state={state}
                    className="flex items-center justify-between p-3 rounded-lg border"
                    style={{ borderColor: `${color}55`, backgroundColor: `${color}10` }}
                  >
                    <span className="text-sm font-semibold text-white">{label}</span>
                    <span
                      className="text-xs font-bold uppercase tracking-widest px-2 py-1 rounded"
                      style={{ backgroundColor: color, color: '#0a0a0a' }}
                    >
                      {FACTOR_STATE_LABEL[state] || state}
                    </span>
                  </li>
                );
              })}
            </ul>
            <p className="text-[11px] text-zinc-500 mt-3">
              Set by NECS Admin. Contact your Regional Manager if you believe a factor is incorrect.
            </p></>}
          </div>
        </div>
      )}
    </>
  );
}
